import { z } from 'zod';
import {
  DocumentStandardPreferences,
  DocumentViewedSchedulePreferences,
  DocumentHasNotBeenViewedSchedulePreferences,
  SendNotificationSignatureSchedule,
} from 'pages/notification-preferences/types';

/**
 * Schema definitions for each preference type
 * These are defined here to avoid circular dependencies
 */
const DocumentStandardPreferencesSchema = z.nativeEnum(DocumentStandardPreferences);
const DocumentViewedSchedulePreferencesSchema = z.nativeEnum(DocumentViewedSchedulePreferences);
const DocumentHasNotBeenViewedSchedulePreferencesSchema = z
  .union([z.nativeEnum(DocumentHasNotBeenViewedSchedulePreferences), z.number().int().min(1).max(31)])
  .transform((value) => {
    if (value === DocumentHasNotBeenViewedSchedulePreferences.ONE_DAY) return 1;
    if (value === DocumentHasNotBeenViewedSchedulePreferences.ONE_WEEK) return 7;
    if (typeof value === 'number') return value;
    return 1;
  });
const SendNotificationSignatureScheduleSchema = z.nativeEnum(SendNotificationSignatureSchedule);

/**
 * Union of all possible preference values
 */
type PreferenceValue =
  | DocumentStandardPreferences
  | DocumentViewedSchedulePreferences
  | DocumentHasNotBeenViewedSchedulePreferences
  | SendNotificationSignatureSchedule
  | boolean
  | number;

/**
 * UI Pattern Types
 * Defines the different UI patterns available for notification preferences
 */
type UIPattern =
  | {
      type: 'standard-document';
      title: string;
      labels: {
        any: string;
        mine: string;
        never: string;
      };
    }
  | {
      type: 'schedule-viewed';
      title: string;
    }
  | {
      type: 'schedule-not-viewed';
      title: string;
      dependsOn: string; // EventKey of parent field
    }
  | {
      type: 'schedule-signature';
      title: string;
    }
  | {
      type: 'boolean-checkbox';
      title: string;
      label: string;
    }
  | {
      type: 'boolean-required';
      title: string;
      label: string;
    }
  | {
      type: 'boolean-hidden';
      title: string;
    };

/**
 * Event Configuration Interface
 * Defines all metadata needed for each notification event
 */
interface EventConfig<T extends PreferenceValue = PreferenceValue> {
  schema: z.ZodSchema<T>;
  default: T;
  category: 'document' | 'signature' | 'expiry';
  ui: UIPattern;
}

/**
 * THE SINGLE SOURCE OF TRUTH FOR ALL NOTIFICATION EVENTS
 *
 * This configuration object defines every notification event in the system.
 * Each event specifies:
 * - schema: Zod validation schema with type inference
 * - default: Default value for the preference
 * - category: Which category the event belongs to (document/signature/expiry)
 * - ui: How the event should be rendered in the UI
 *
 * To add a new event, simply add a new entry to this object.
 * All other values (schemas, UI options, defaults, mappings) are derived from this config.
 */
export const EVENT_CONFIG = {
  // ============= DOCUMENT EVENTS =============

  document_sent: {
    schema: DocumentStandardPreferencesSchema,
    default: DocumentStandardPreferences.MINE,
    category: 'document',
    ui: {
      type: 'standard-document',
      title: 'Document Sent',
      labels: {
        any: "Any document's status is changed to sent",
        mine: "My document's status is changed to sent",
        never: 'Do not notify me when documents are sent',
      },
    },
  },

  document_viewed: {
    schema: DocumentStandardPreferencesSchema,
    default: DocumentStandardPreferences.MINE,
    category: 'document',
    ui: {
      type: 'standard-document',
      title: 'Document Viewed',
      labels: {
        any: 'Any document is viewed',
        mine: 'One of my documents is viewed',
        never: 'Do not notify me when documents are viewed',
      },
    },
  },

  document_viewed_schedule: {
    schema: DocumentViewedSchedulePreferencesSchema,
    default: DocumentViewedSchedulePreferences.EACH_TIME_UNIQUE,
    category: 'document',
    ui: {
      type: 'schedule-viewed',
      title: 'Document Viewed',
    },
  },

  document_has_not_been_viewed: {
    schema: DocumentStandardPreferencesSchema,
    default: DocumentStandardPreferences.MINE,
    category: 'document',
    ui: {
      type: 'standard-document',
      title: 'Document has not been viewed',
      labels: {
        any: 'Any document has not been viewed by someone outside of Proposify',
        mine: 'One of my documents has not been viewed by someone outside of Proposify',
        never: 'Do not notify me when documents are not viewed',
      },
    },
  },

  document_has_not_been_viewed_schedule: {
    schema: DocumentHasNotBeenViewedSchedulePreferencesSchema,
    default: 7,
    category: 'document',
    ui: {
      type: 'schedule-not-viewed',
      title: 'Document has not been viewed',
      dependsOn: 'document_has_not_been_viewed',
    },
  },

  document_commented: {
    schema: DocumentStandardPreferencesSchema,
    default: DocumentStandardPreferences.MINE,
    category: 'document',
    ui: {
      type: 'standard-document',
      title: 'Document Commented',
      labels: {
        any: 'Any document has been commented on',
        mine: 'One of my documents has been commented on',
        never: 'Do not notify me when documents are commented on',
      },
    },
  },

  // ============= SIGNATURE EVENTS =============

  document_signed: {
    schema: DocumentStandardPreferencesSchema,
    default: DocumentStandardPreferences.MINE,
    category: 'signature',
    ui: {
      type: 'standard-document',
      title: 'Document signed',
      labels: {
        any: 'Any document is signed',
        mine: 'One of my documents is signed',
        never: 'Do not notify me when documents are signed',
      },
    },
  },

  document_signed_schedule: {
    schema: SendNotificationSignatureScheduleSchema,
    default: SendNotificationSignatureSchedule.ONLY_AFTER,
    category: 'signature',
    ui: {
      type: 'schedule-signature',
      title: 'Document signed',
    },
  },

  document_voided: {
    schema: z.boolean(),
    default: false,
    category: 'signature',
    ui: {
      type: 'boolean-hidden',
      title: 'Document voided',
    },
  },

  signature_requested: {
    schema: z.boolean(),
    default: true,
    category: 'signature',
    ui: {
      type: 'boolean-required',
      title: 'Signature Requested (required)',
      label: 'My signature has been requested on a document',
    },
  },

  // ============= EXPIRY EVENTS =============

  document_expired: {
    schema: DocumentStandardPreferencesSchema,
    default: DocumentStandardPreferences.MINE,
    category: 'expiry',
    ui: {
      type: 'standard-document',
      title: 'Document expired',
      labels: {
        any: 'Any document expires',
        mine: 'One of my documents expires',
        never: 'Do not notify me when documents expire',
      },
    },
  },

  document_expired_day_before: {
    schema: z.boolean(),
    default: true,
    category: 'expiry',
    ui: {
      type: 'boolean-checkbox',
      title: 'Document expired',
      label: 'The day before the document expires',
    },
  },

  document_expired_a_week_before: {
    schema: z.boolean(),
    default: false,
    category: 'expiry',
    ui: {
      type: 'boolean-checkbox',
      title: 'Document expired',
      label: 'A week before the document expires',
    },
  },
} as const satisfies Record<string, EventConfig>;

export type EventKey = keyof typeof EVENT_CONFIG;

export type EventSchemaType<K extends EventKey> = (typeof EVENT_CONFIG)[K]['schema'];

export type EventDefaultType<K extends EventKey> = (typeof EVENT_CONFIG)[K]['default'];
